import { useEffect, useMemo, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Plus, Trash2 } from "lucide-react";
import { SettingsAPI } from "@/lib/api";
import {
  emptyProfile,
  DEFAULT_TOU_PERIOD,
  DEFAULT_TIER,
  summarizeProfile,
  MONTH_OPTIONS,
} from "@/lib/chargingRates";

/**
 * ChargingRatesSection — the Home + Work rate config blocks. Saves to
 * `settings.charging_rates` on the server, debounced ~600ms after the last
 * change. Each location block is independent; 'Other' is intentionally
 * omitted (public charger prices are too variable to auto-fill).
 */

const PROFILE_TYPES = [
  { value: "flat", label: "Flat" },
  { value: "tiered", label: "Tiered" },
  { value: "tou", label: "Time-of-Use" },
  { value: "monthly", label: "Monthly" },
];

const DAY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekends", label: "Weekends" },
];

const PERIOD_COLORS = ["green", "red", "amber", "blue"];
const COLOR_HEX = { green: "#34D17A", red: "#FF5A5F", amber: "#FFB340", blue: "#5AB1FF" };

const MAX_PERIODS = 4;

const ensureProfile = (p) => ({ ...emptyProfile(), ...(p || {}) });

export default function ChargingRatesSection({ initial, currencySymbol = "$" }) {
  const [home, setHome] = useState(ensureProfile(initial?.home));
  const [work, setWork] = useState(ensureProfile(initial?.work));
  const [saving, setSaving] = useState(false);

  // Debounced server write — coalesce rapid keystrokes into a single PUT.
  useEffect(() => {
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await SettingsAPI.update({ charging_rates: { home, work } });
      } catch {
        /* silent — the next change will retry */
      } finally {
        setSaving(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [home, work]);

  return (
    <div className="space-y-3" data-testid="charging-rates-section">
      <div className="flex items-center gap-2">
        <Zap className="w-3.5 h-3.5 text-[#FFB340]" strokeWidth={2} fill="#FFB340" />
        <Label className="label-tiny !text-[#FFB340]">Charging Rates</Label>
        {saving && (
          <span className="text-[10px] tracking-wider uppercase text-[var(--text-faint)] ml-auto">
            Saving…
          </span>
        )}
      </div>
      <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
        Set a rate for Home and Work charging. Cost auto-fills when you log a session at that location.
        Other sessions are always entered manually. Saved instantly to this account.
      </p>

      <ProfileBlock
        title="Home Rate"
        emoji="🏠"
        accent="#34D17A"
        accentSoftBg="rgba(52, 209, 122, 0.08)"
        accentSoftBorder="rgba(52, 209, 122, 0.25)"
        profile={home}
        onChange={setHome}
        testIdPrefix="home-rate"
        currencySymbol={currencySymbol}
      />
      <ProfileBlock
        title="Work Rate"
        emoji="🏢"
        accent="#5AB1FF"
        accentSoftBg="rgba(90, 177, 255, 0.08)"
        accentSoftBorder="rgba(90, 177, 255, 0.25)"
        profile={work}
        onChange={setWork}
        testIdPrefix="work-rate"
        currencySymbol={currencySymbol}
      />

      <p className="text-[11px] text-[var(--text-muted)] leading-relaxed pt-1">
        📍 <span className="text-[var(--text-secondary)]">Other</span> — no rate configured. Public charger
        costs vary per session and are always entered manually.
      </p>
    </div>
  );
}

function ProfileBlock({ title, emoji, accent, accentSoftBg, accentSoftBorder, profile, onChange, testIdPrefix, currencySymbol }) {
  const summary = useMemo(() => summarizeProfile(profile), [profile]);

  const setProfileType = (t) => {
    if (t === "tou" && (!profile.tou_periods || profile.tou_periods.length === 0)) {
      onChange({ ...profile, profile_type: t, tou_periods: [DEFAULT_TOU_PERIOD()] });
    } else if (t === "tiered" && (!profile.tiers || profile.tiers.length < 2)) {
      // Default tiers: one "Up to N kWh" + one "Remaining". Full-year window.
      onChange({
        ...profile,
        profile_type: t,
        tiers: [DEFAULT_TIER(false), DEFAULT_TIER(true)],
        tier_start_month: profile.tier_start_month || 1,
        tier_end_month: profile.tier_end_month || 12,
      });
    } else {
      onChange({ ...profile, profile_type: t });
    }
  };

  return (
    <div
      className="border px-4 pt-3 pb-4 space-y-3"
      style={{ background: accentSoftBg, borderColor: accentSoftBorder, borderRadius: 4 }}
      data-testid={`${testIdPrefix}-block`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{emoji}</span>
          <span className="text-sm font-medium tracking-tight" style={{ color: accent }}>{title}</span>
        </div>
        {summary && (
          <span className="text-[11px] tracking-wider font-mono-data" style={{ color: accent }}>
            {summary.label} · {summary.detail}
          </span>
        )}
      </div>

      {/* Profile type segmented control */}
      <div
        className="inline-flex w-full items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5"
        style={{ borderRadius: 2 }}
      >
        {PROFILE_TYPES.map(({ value, label }) => {
          const active = profile.profile_type === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setProfileType(value)}
              data-testid={`${testIdPrefix}-type-${value}`}
              aria-pressed={active}
              className={`flex-1 px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase transition-colors ${
                active
                  ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }`}
              style={{ borderRadius: 2 }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {profile.profile_type === "flat" && (
        <FlatEditor profile={profile} onChange={onChange} testIdPrefix={testIdPrefix} currencySymbol={currencySymbol} />
      )}
      {profile.profile_type === "tou" && (
        <TouEditor profile={profile} onChange={onChange} testIdPrefix={testIdPrefix} />
      )}
      {profile.profile_type === "tiered" && (
        <TieredEditor profile={profile} onChange={onChange} testIdPrefix={testIdPrefix} currencySymbol={currencySymbol} />
      )}
      {profile.profile_type === "monthly" && (
        <MonthlyEditor profile={profile} onChange={onChange} testIdPrefix={testIdPrefix} currencySymbol={currencySymbol} />
      )}
    </div>
  );
}

function FlatEditor({ profile, onChange, testIdPrefix, currencySymbol }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-tiny">Rate per kWh</Label>
      <div className="relative">
        <Input
          type="number"
          step="0.001"
          min="0"
          inputMode="decimal"
          value={profile.flat_rate_per_kwh ?? ""}
          onChange={(e) => onChange({ ...profile, flat_rate_per_kwh: e.target.value })}
          placeholder="0.12"
          className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data pr-16"
          data-testid={`${testIdPrefix}-flat-input`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tracking-wider text-[var(--text-faint)] font-mono-data">
          {currencySymbol}/kWh
        </span>
      </div>
    </div>
  );
}

function TouEditor({ profile, onChange, testIdPrefix }) {
  const periods = profile.tou_periods || [];
  const setPeriod = useCallback(
    (idx, patch) => {
      const next = periods.map((p, i) => (i === idx ? { ...p, ...patch } : p));
      onChange({ ...profile, tou_periods: next });
    },
    [periods, profile, onChange]
  );
  const removePeriod = (idx) =>
    onChange({ ...profile, tou_periods: periods.filter((_, i) => i !== idx) });
  const addPeriod = () => {
    if (periods.length >= MAX_PERIODS) return;
    const nextColor = PERIOD_COLORS[periods.length % PERIOD_COLORS.length];
    onChange({
      ...profile,
      tou_periods: [
        ...periods,
        { ...DEFAULT_TOU_PERIOD(), label: periods.length === 0 ? "Off-Peak" : "On-Peak", color: nextColor },
      ],
    });
  };

  return (
    <div className="space-y-2.5">
      {periods.map((p, idx) => (
        <PeriodRow
          key={p.id || idx}
          period={p}
          idx={idx}
          onChange={(patch) => setPeriod(idx, patch)}
          onRemove={() => removePeriod(idx)}
          canRemove={periods.length > 1}
          testIdPrefix={`${testIdPrefix}-tou-${idx}`}
        />
      ))}
      {periods.length < MAX_PERIODS && (
        <button
          type="button"
          onClick={addPeriod}
          className="text-[12px] text-[#FFB340] hover:text-[#FFA500] tracking-tight transition-colors"
          data-testid={`${testIdPrefix}-tou-add`}
        >
          + Add Period
        </button>
      )}
    </div>
  );
}

function PeriodRow({ period, onChange, onRemove, canRemove, testIdPrefix }) {
  return (
    <div
      className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-3 pt-2.5 pb-3 space-y-2"
      style={{ borderRadius: 3 }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: COLOR_HEX[period.color] || COLOR_HEX.green }}
          />
          <Input
            value={period.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Off-Peak"
            className="bg-transparent border-0 px-0 h-7 text-[13px] text-[var(--text-primary)] focus-visible:ring-0"
            data-testid={`${testIdPrefix}-label`}
          />
        </div>
        <select
          value={period.days || "all"}
          onChange={(e) => onChange({ days: e.target.value })}
          className="text-[11px] tracking-wider bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] border-0 focus:outline-none cursor-pointer"
          data-testid={`${testIdPrefix}-days`}
        >
          {DAY_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[var(--text-faint)] hover:text-[#E82127]"
            data-testid={`${testIdPrefix}-remove`}
            aria-label="Remove period"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="label-tiny">$/kWh</Label>
          <Input
            type="number"
            step="0.001"
            min="0"
            inputMode="decimal"
            value={period.rate ?? ""}
            onChange={(e) => onChange({ rate: e.target.value })}
            placeholder="0.07"
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs mt-1"
            data-testid={`${testIdPrefix}-rate`}
          />
        </div>
        <div>
          <Label className="label-tiny">From</Label>
          <Input
            type="time"
            value={period.start}
            onChange={(e) => onChange({ start: e.target.value })}
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs mt-1"
            data-testid={`${testIdPrefix}-start`}
          />
        </div>
        <div>
          <Label className="label-tiny">To</Label>
          <Input
            type="time"
            value={period.end}
            onChange={(e) => onChange({ end: e.target.value })}
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs mt-1"
            data-testid={`${testIdPrefix}-end`}
          />
        </div>
      </div>
    </div>
  );
}

function MonthlyEditor({ profile, onChange, testIdPrefix, currencySymbol }) {
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-monthly-editor`}>
      <div className="space-y-2">
        <Label className="label-tiny">Monthly Cost ({currencySymbol})</Label>
        <div className="relative">
          <Input
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={profile.monthly_cost ?? ""}
            onChange={(e) => onChange({ ...profile, monthly_cost: e.target.value })}
            placeholder="45.00"
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data pr-14"
            data-testid={`${testIdPrefix}-monthly-input`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tracking-wider text-[var(--text-faint)] font-mono-data">
            {currencySymbol}/mo
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="label-tiny">Start Date</Label>
          <Input
            type="date"
            value={profile.monthly_start_date || ""}
            onChange={(e) => onChange({ ...profile, monthly_start_date: e.target.value })}
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs"
            data-testid={`${testIdPrefix}-monthly-start-date`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-tiny">End Date <span className="text-[var(--text-faint)] normal-case">· optional</span></Label>
          <Input
            type="date"
            value={profile.monthly_end_date || ""}
            onChange={(e) => onChange({ ...profile, monthly_end_date: e.target.value })}
            placeholder="Ongoing"
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs"
            data-testid={`${testIdPrefix}-monthly-end-date`}
          />
        </div>
      </div>
      <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
        Subscription cost is added to Charging Spend for every calendar month between Start and End (or ongoing if End is blank), regardless of sessions logged. Individual sessions still auto-fill to $0.00.
      </p>
    </div>
  );
}

const MAX_TIERS = 5;

function TieredEditor({ profile, onChange, testIdPrefix, currencySymbol }) {
  const tiers = profile.tiers || [];

  const updateTier = (idx, patch) => {
    const next = tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    onChange({ ...profile, tiers: next });
  };
  const addTier = () => {
    if (tiers.length >= MAX_TIERS) return;
    // Insert a new numeric tier just before the trailing "Remaining" row.
    const head = tiers.slice(0, -1);
    const tail = tiers.slice(-1);
    onChange({ ...profile, tiers: [...head, DEFAULT_TIER(false), ...tail] });
  };
  const removeTier = (idx) => {
    // Always keep at least 2 rows (one numeric + the trailing "Remaining").
    if (tiers.length <= 2) return;
    const next = tiers.filter((_, i) => i !== idx);
    onChange({ ...profile, tiers: next });
  };

  return (
    <div className="space-y-2.5" data-testid={`${testIdPrefix}-tiered-editor`}>
      <div className="grid grid-cols-[1fr_120px_24px] gap-2 text-[10px] tracking-[0.14em] uppercase text-[var(--text-faint)] px-1">
        <span>Threshold</span>
        <span>{currencySymbol}/kWh</span>
        <span />
      </div>
      {tiers.map((tier, idx) => {
        const isLast = idx === tiers.length - 1;
        const canRemove = !isLast && tiers.length > 2;
        return (
          <div
            key={tier.id || idx}
            className="grid grid-cols-[1fr_120px_24px] gap-2 items-center"
            data-testid={`${testIdPrefix}-tier-${idx}`}
          >
            {isLast ? (
              <div
                className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-3 h-9 text-xs text-[var(--text-secondary)] tracking-tight flex items-center"
                style={{ borderRadius: 2 }}
              >
                Remaining kWh
              </div>
            ) : (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] tracking-wider text-[var(--text-faint)] pointer-events-none">
                  Up to
                </span>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  inputMode="decimal"
                  value={tier.threshold ?? ""}
                  onChange={(e) => updateTier(idx, { threshold: e.target.value })}
                  placeholder="500"
                  className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs pl-12 pr-10"
                  data-testid={`${testIdPrefix}-tier-${idx}-threshold`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tracking-wider text-[var(--text-faint)] font-mono-data pointer-events-none">
                  kWh
                </span>
              </div>
            )}
            <Input
              type="number"
              step="0.001"
              min="0"
              inputMode="decimal"
              value={tier.rate ?? ""}
              onChange={(e) => updateTier(idx, { rate: e.target.value })}
              placeholder="0.08"
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 text-xs"
              data-testid={`${testIdPrefix}-tier-${idx}-rate`}
            />
            {canRemove ? (
              <button
                type="button"
                onClick={() => removeTier(idx)}
                className="text-[var(--text-faint)] hover:text-[#E82127] flex items-center justify-center h-9"
                aria-label="Remove tier"
                data-testid={`${testIdPrefix}-tier-${idx}-remove`}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            ) : (
              <span />
            )}
          </div>
        );
      })}
      {tiers.length < MAX_TIERS && (
        <button
          type="button"
          onClick={addTier}
          className="text-[12px] text-[#FFB340] hover:text-[#FFA500] tracking-tight transition-colors flex items-center gap-1"
          data-testid={`${testIdPrefix}-tier-add`}
        >
          <Plus className="w-3 h-3" strokeWidth={2} /> Add Tier
        </button>
      )}

      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] pt-1">
        <span className="text-[var(--text-muted)]">Applies from</span>
        <select
          value={profile.tier_start_month ?? 1}
          onChange={(e) => onChange({ ...profile, tier_start_month: parseInt(e.target.value, 10) })}
          className="bg-[var(--bg-input)] border border-[var(--border-subtle)] text-[var(--text-primary)] h-7 px-2 text-xs cursor-pointer"
          data-testid={`${testIdPrefix}-tier-start-month`}
          style={{ borderRadius: 2 }}
        >
          {MONTH_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <span className="text-[var(--text-muted)]">to</span>
        <select
          value={profile.tier_end_month ?? 12}
          onChange={(e) => onChange({ ...profile, tier_end_month: parseInt(e.target.value, 10) })}
          className="bg-[var(--bg-input)] border border-[var(--border-subtle)] text-[var(--text-primary)] h-7 px-2 text-xs cursor-pointer"
          data-testid={`${testIdPrefix}-tier-end-month`}
          style={{ borderRadius: 2 }}
        >
          {MONTH_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
